import { IconButton, Typography } from "@mui/material";
import { FaChevronLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

import { Container } from "../../common/Container/Container";
import { BottomNavigator } from "./components/ButtonNavigator";
import { LeftMenu } from "./components/LeftMenu";

export function HowToDeliverAGreatService() {
	const navigate = useNavigate();

	return (
		<Container navbarOrkaProps={{ white: true }} noFooter>
			<div className="resource-detail-container">
				<LeftMenu />
				<div className="main-content">
					<div className="resource-header">
						<IconButton
							className="icon-button back-button"
							onClick={() => {
								navigate("/resources");
							}}>
							<FaChevronLeft />
						</IconButton>
						<Typography className="normal-text">Back to Resources</Typography>
					</div>

					<Typography
						className="title-text bold resource-title"
						textAlign="center"
						id="introduction">
						How to Deliver a Great Service?
					</Typography>

					<Typography className="normal-text paragraph-top">
						Many flight schools are in the business of selling flight training,
						not teaching. If you are an independent flight instructor, you want
						to continue differentiating yourself. The first step in delivering
						great service is helping students determine if you are the right
						instructor for them. You can prepare and deliver the best steak to
						the table, but if the person wanted white fish, well...
					</Typography>

					<Typography className="normal-text paragraph-top">
						We want to communicate exactly what we offer, how we offer it, and
						why we do it the way we do. How can you accomplish that? You need a
						framework or worksheet to help you address all the elements that
						define your service. Our course builder has distilled the most
						essential elements:
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="identify-the-value-you-deliver"
							href="#identify-the-value-you-deliver">
							Identify the value you deliver
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Students are looking for help, yes, but they are looking for
						specific help. At the end of the day, the purpose of flight training
						is to transfer specific knowledge and skills. Suppose a student
						trains with you—so what? What specific problems are you helping them
						overcome? What results are you helping them achieve? What specific
						knowledge and skills are you transferring? What types of content and
						equipment do you include?
					</Typography>

					<Typography className="normal-text paragraph-top">
						Students want to be able to do something. Taking a minute to answer
						these questions allows you to communicate the specific value you can
						deliver. Your thoughtfulness and clarity in answering these
						questions help best-fit students confirm they are in the right
						place, and eager to hear more, get in touch, or get started. This
						process creates a bridge to form connections that are a great match!
					</Typography>

					<Typography className="normal-text paragraph-top">
						Once you identify the "what," what you are enabling your students to
						do, narrow it down into a tagline that you will use to refer to your
						service.
					</Typography>

					<Typography className="normal-text paragraph-top">
						How to write a tagline? Keep it short—five words or less is ideal.
						This tag or name should be in language your students can understand.
						Avoid aviation jargon and stay focused on the outcome your students
						aspire to. Try finishing the sentence: "Imagine you could do… what?"
					</Typography>

					<Typography className="normal-text paragraph-top">
						But telling is not good enough. Imagine you drive to an airport to
						meet an instructor who offers the training you were looking for.
						What images do you see? Perhaps the instructor has photos in their
						hangar? Perhaps you see the aircraft to be used or take an inside
						look at the aircraft? Try to create an airport-like experience, or
						the real experience if not meeting at an airport. If you have photos
						engaging with your students, those rock! Include student success
						memories if you have them. You get up to nine media files, which can
						include a video and all kinds of images.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Altogether, this is like waving a flag that your kind of students
						are longing to find.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="prerequisites"
							href="#prerequisites">
							Prerequisites
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Students may be asking themselves, "Is the training at a level where
						I can engage and make progress?" You can't help everybody. We should
						identify the showstoppers that will prevent us from helping people.
						Spelling out deal breakers upfront prevents heartbreaks and wasted
						time. Students may be eager to receive our training, but if they
						exceed the allowable weight limit, for example, we can't help them.
						If you are offering an accelerated certification course, it would be
						appropriate to require a passing written test result. If you are
						teaching an add-on rating or a specialty course, you will need the
						student to already hold a pilot certificate. Minimum age could also
						be something you want to include if you are certifying initial
						pilots, as it would be inefficient to train someone who won't be
						able to take the test right after completion or within a reasonable
						time.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="logistics"
							href="#logistics">
							Logistics
						</Typography>
					</div>

					<div className="first-paragraph-top">
						<Typography
							className="big-text bold black-text underline-on-hover-link"
							component="a"
							id="location"
							href="#location">
							Location
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Do you meet your students in person, or is it all online? If you
						meet your students in person, you can enter the address of a
						specific location or a general area if you travel to multiple
						locations. If you are willing to travel all over the USA, you can
						simply enter "USA." If you are willing to travel worldwide, note
						that in your course description.
					</Typography>

					<div className="subsection-top">
						<Typography
							className="big-text bold paragraph-top black-text underline-on-hover-link"
							component="a"
							id="pace"
							href="#pace">
							Pace
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Students want to make progress. Once the student starts, how often
						do you need to train? Daily or just twice per week? You already know
						the ideal training frequency for the most effective learning to take
						place and for progression toward the goals you defined. Defining
						this ideal training frequency upfront sets the recommended pace.
						After all, if students can't stick to the recommended pace, they
						risk not getting what the course was designed to do for them.
					</Typography>

					<Typography className="normal-text paragraph-top">
						A cool hack is that we use this recommended pace to help your
						students schedule their appointments. With your calendar features,
						we know your availability as if we were your personal assistant.
						When the time comes to schedule your service, we give students a
						program itinerary or schedule that follows your recommended pace so
						that they can make optimal progress. If, for any reason, a student
						can't follow the recommended pace or accept the dates we recommend,
						they can make adjustments that fall within your calendar
						availability. Yes! This input helps us automate the task of
						scheduling, matching both your and your student's availability.
						Scheduling all classes upfront also increases student commitment.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Of course, if weather, maintenance, or something else happens, you
						can reschedule appointments yourself as the instructor. Students, on
						the other hand, cannot edit appointments once made without talking
						with you. As a trainer, scheduling all course appointments upfront
						in series ensures student commitment to show up with the consistency
						required to achieve the course or service goals.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="classes"
							href="#classes">
							Classes
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Why are you helping your students with this? Seriously, what is the
						outcome you can produce? Arranging training to make it all make
						sense is up to you. As flight instructors, we guide students to
						different destinations. Remember the "what?" Students chose you
						because you made a promise - a promise to enable them to do
						something, to acquire specific knowledge and skills. Such knowledge
						and skills are the destination. Students must pass through classes
						or sessions as they acquire these skills. Each class should be
						created as an essential part of achieving the end goal. Together,
						all classes become the path or the bridge to the knowledge and
						skills you wish to impart. You may call a series of classes the
						curriculum, syllabus, program, or simply the scope of your service.
					</Typography>

					<Typography className="normal-text paragraph-top">
						Class by class, your students are becoming all they can be and
						transforming their skills. Without thoughtfulness at this level, you
						will end up wasting time and effort and very likely fail to deliver
						on your promise. This is the meat of the meal, where your motivation
						and sincerity to help your students allows you to prepare the plan
						to help them achieve their goals.{" "}
					</Typography>

					<Typography className="normal-text paragraph-top">
						Another cool hack is that creating classes allows us to help you
						schedule students and collect payments. Because if you can't
						efficiently schedule clients and collect payments, nothing else
						matters. You won't be able to make a living as an independent
						trainer if you get this part wrong.
					</Typography>

					<div className="section-top">
						<Typography
							className="small-title-text bold black-text underline-on-hover-link"
							component="a"
							id="ready-to-build-a-great-service"
							href="#ready-to-build-a-great-service">
							Ready to build a great service?
						</Typography>
					</div>

					<Typography className="normal-text first-paragraph-top">
						Why are you helping your students with this? Seriously, what is the
						outcome you can produce? Arranging training to make it all make
						sense is up to you. As flight instructors, we guide students to
						different destinations. Remember the "what?" Students chose you
						because you made a promise - a promise to enable them to do
						something, to acquire specific knowledge and skills. Such knowledge
						and skills are the destination. Students must pass through classes
						or sessions as they acquire these skills. Each class should be
						created as an essential part of achieving the end goal. Together,
						all classes become the path or the bridge to the knowledge and
						skills you wish to impart. You may call a series of classes the
						curriculum, syllabus, program, or simply the scope of your service.
					</Typography>

					<BottomNavigator />
				</div>
				<div className="right-menu">
					<Typography className="big-text bold">Table of Contents</Typography>
					<div className="paragraph-top">
						<Typography
							component="a"
							href="#introduction"
							className="normal-text black-text underline-on-hover-link">
							Introduction
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#identify-the-value-you-deliver"
							className="normal-text black-text underline-on-hover-link">
							Identify the value you deliver
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#prerequisites"
							className="normal-text black-text underline-on-hover-link">
							Prerequisites
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#logistics"
							className="normal-text black-text underline-on-hover-link">
							Logistics
						</Typography>
					</div>
					<div className="spacing-left">
						<Typography
							component="a"
							href="#location"
							className="small-text black-text underline-on-hover-link">
							Location
						</Typography>
					</div>
					<div className="spacing-left">
						<Typography
							component="a"
							href="#pace"
							className="small-text black-text underline-on-hover-link">
							Pace
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#classes"
							className="normal-text black-text underline-on-hover-link">
							Classes
						</Typography>
					</div>
					<div>
						<Typography
							component="a"
							href="#ready-to-build-a-great-service"
							className="normal-text black-text underline-on-hover-link">
							Ready to build a great service?
						</Typography>
					</div>
				</div>
			</div>
		</Container>
	);
}
